import { Dispatch, RefObject, SetStateAction } from "react";

import { APIListValuesRequest } from "shared/api/api";
import { DataType, SortBy } from "shared/types";

import { AssociatedVehiclesState } from "pages/shared/analytics/associatedVehicles/types";

import { SelectedChartOptions } from "features/ui/charts/Actions/types";
import { SelectOption } from "features/ui/Select";

import { BrowserEventEmitter } from "./BrowserEventEmitter";
import { FilterGroupState, FilterRowState } from "./FilterBuilder/types";

/**
 * @interface FilterSchemaItem
 * @member {string} label label to display on top of filter
 * @member {boolean} fieldName API field identifier
 * @member {boolean?} search whether filter should hit API whenever search query is changed
 * @member {boolean?} loadDataOnOpen whether filter should load data when dropdown is opened but search query is empty
 * @member {boolean?} multiple whether to allow user to select multiple values
 * @member {number?} maxValues maximum number of values that can be selected. If not set, there is no limit
 * @member {function?} loadOptionsFunc function that loads initial values for dropdown
 * @member {function?} transformInitialSelectedFunc function that converts raw data (like IDs) to human readable format (like label/description)
 * @member {function?} formatLabelFunc function that formats value in appropriate format and adds a label
 * @member {boolean} description description that appears in the tooltip next to label
 * @member {JSX.Element | string | undefined} filterType filter type in case we want to override the SearchSelect type
 * @member {string?} fieldNameForAPI if field on values endpoints do not match the one in the table, we can override it with setting custom field name
 * @member {boolean?} disableFiltering if we do not want to apply filters from current table for getting values for dropdown
 * @member {boolean?} disableSelectFilters if we do not want to allow "in" and "not in" operators for this filter and thus SearchSelect filter
 * @member {boolean?} disableContainsFilters if we do not want to allow "Contains" operators for this filter
 * @member {boolean?} disableIsEmptyFilters if we do not want to allow "Is empty" and "Is not empty" operators for this filter
 * @member {boolean?} disableStartsWithFilters if we do not want to allow "Starts with" and "Does not start with" operators for this filter
 * @member {boolean?} disableArbitraryText if we do not want to allow custom text to be typed as filter value in SelectFilter (freeSolo)
 * @member {boolean?} enableMinMaxFilters if we want to allow min/max filter for this filter
 * @member {boolean | string?} includeEmptyOption if true adds an "Empty" option to the dropdown that gets converted to IS_EMPTY operator, if string uses that as the label (e.g. "Unassigned")
 */
export interface FilterSchemaItem {
  label: string;
  fieldName: string;
  valuesEndpoint?: string;
  search?: boolean;
  loadDataOnOpen?: boolean;
  multiple?: boolean;
  maxValues?: number;
  loadOptionsFunc?: (args: APIListValuesRequest) => Promise<SelectOption[]>;
  transformInitialSelectedFunc?: (
    initialSelected: SelectOption[]
  ) => Promise<SelectOption[]>;
  getValueNameFunc?: (id: string) => Promise<string>;
  formatLabelFunc?: (selected: SelectOption[]) => SelectOption[];
  loadOptionsArgs?: Record<string, any>;
  description?: JSX.Element | string;
  filterType?: FilterType;
  filterDataType?: DataType;
  fieldNameForAPI?: string;
  disableFiltering?: boolean;
  disableSelectFilters?: boolean;
  disableContainsFilters?: boolean;
  disableIsEmptyFilters?: boolean;
  disableStartsWithFilters?: boolean;
  disableIsNotFilteredFilters?: boolean;
  enableMinMaxFilters?: boolean;
  disableArbitraryText?: boolean;
  whitelistedFilterOperators?: FilterOperator[];
  onlyAllowPositiveIntegers?: boolean;
  customFilter?: FilterGroupState;
  baseEntityText?: string;
  disableSelectingWindowDirection?: boolean;
  relationEndpoint?: string;
  isCount?: boolean;
  includeEmptyOption?: boolean | string;
}

export const enum FilterOperator {
  NOT_FILTERED = "not_filtered",
  IN = "in",
  NOT_IN = "not_in",
  IS_EMPTY = "is_empty",
  IS_NOT_EMPTY = "is_not_empty",
  GREATER_THAN = "greater_than",
  GREATER_OR_EQUAL = "greater_or_equal",
  LESS_THAN = "less_than",
  LESS_OR_EQUAL = "less_or_equal",
  BETWEEN = "between",
  CONTAINS = "contains",
  NOT_CONTAINS = "not_contains",
  MATCH = "match",
  IN_LAST = "in_last",
  STARTS_WITH = "starts_with",
  NOT_STARTS_WITH = "not_starts_with",
  IS_TRUE = "is_true",
  IS_FALSE = "is_false",
  EQUALS = "eq",
  NOT_EQUALS = "neq",
  OCCURS = "occurs",
  NOT_OCCURS = "noccurs",
  EXISTS = "exists",
  NOT_EXISTS = "nexists",
  HAS_ONE_OR_MORE = "has_one_or_more",
  HAS_NONE = "has_none",
}

export const EMPTY_FILTER_VALUE = "__empty__";

export type FilterState = {
  [key: string]: SingleFilterState;
};

export type OccursFilterWindowDirectionType = OccursFilterWindowDirection;

export type OccursFilterOptionKeys =
  "windowSize" | "windowType" | "windowDirection" | "dateField";

export enum OccursFilterWindowDirection {
  BEFORE = "before",
  AFTER = "after",
  BOTH = "both",
}

export type OccursFilterState = {
  filters: string;
  windowSize: number;
  windowType: string;
  windowDirection: OccursFilterWindowDirection;
  dateField?: string;
};

export type OccursWindowOptions = {
  windowSize: string | number;
  windowType: string;
  windowDirection: OccursFilterWindowDirection;
  dateField?: string;
};

export type SingleFilterState = {
  values: string[];
  operator: FilterOperator;
  extra?: Record<string, string>;
};

export enum FilterType {
  STRING = "string",
  NUMBER = "number",
  DATE = "date",
  BOOLEAN = "boolean",
  DURATION = "duration",
  EXISTS = "exists",
  OCCURS = "occurs",
  RELATION = "relation",
}

export type FilterChangeProps = {
  key: string;
  op_id: FilterOperator;
  values?: string[];
  dataType?: DataType;
  outsideChange?: boolean;
  replaceAttribute?: boolean;
};

export type FilterChangeCallback = (props: FilterChangeProps) => void;
export type onInputChangeCallback = (inputValue: string) => void;

export interface ChartSettingsChangeHandler {
  (chartSettings: ChartSettingsState, chartKey: string): void;
}

export type FilterOverviewFormat = "badge" | "label-inline";
export type FilterValue = string | JSX.Element;

export interface FilterEntry {
  fieldName: string;
  operator: FilterOperator;
  fieldValue: FilterValue;
}

export type InitialDateValues = (string | null)[];

export interface UseFilterSortStateProps {
  pageKey: string;
  // This is the list of attributes that are available for the current data type.
  // We ll remove any attributes that are not in this list from filters/sorts automatically.
  // This is useful because users can have certain filter enabled, but admins can disable one or more
  // attributes, and we want to remove any attributes from filters that aren't valid.
  // See attributeAccessors in UseSchema -> thats the list of attributes that are available for the current data type.
  // Note that if we pass empty array here, we'll ignore this check and keep all filters.
  schemaAttributes: string[];
  filterableAttributes?: string[];
  sortableAttributes?: string[];
  defaultSort?: SortBy;
  disableUsingQuery?: boolean;
  disableUsingLocalStorage?: boolean;
  defaultFilterValues?: FilterGroupState;
  defaultTab?: string;
  defaultAssociatedVehiclesState?: AssociatedVehiclesState;
  onlyAdvancedFilters?: boolean;
  // the static filters are applied silently without being shown in the UI
  staticFilters?: FilterGroupState;
  searchFields?: string[];
}

export interface TableSearch {
  value: string;
  onChange: (value: string) => void;
}

export interface FilterSortState {
  filters?: FilterGroupState;
  quickFilters?: FilterRowState[];
  sort?: SortBy;
  columns?: string[];
  chartSettings?: PageChartSettingsState;
  relatedSignalEventsFilter?: OccursFilterState;
  isAdvancedEditor?: boolean;
  associatedVehiclesState?: AssociatedVehiclesState;
}

/**
 * The settings for a single chart.
 * There can be any number of settings for a single chart, things like yAxis, xAxis, granularity, etc.
 * Each single setting has two properties:
 * - id: The id of the setting, which is typically a ChartAction.
 *   An example would be the id "y" from this action from the ClaimsChart:
 *   {
 *     id: "y",
 *     title: yAxis,
 *     type: "dropdownSelect",
 *     options: yAxisOptions,
 *   }
 * - optionId: The id of the option that is selected for the corresponding setting.
 *   An example would be the id "rollingIPTV" from this option from the ClaimsChart:
 *   {
 *     id: "rollingIPTV",
 *     value: "Rolling 7-day IPTV",
 *     label: "Rolling 7-day IPTV",
 *   }
 * An example of the complete type would be:
 * [
 *   {
 *     id: "y",
 *     optionId: "rollingIPTV",
 *   },
 * ],
 *
 * This is the level of data that is sent to the manageChartSettingsChange() function.
 */
type ChartSettingsState = SelectedChartOptions[];

/**
 * The settings for the charts on a tab.
 * There can be any number of settings for a chart, and any number of charts on a tab.
 * The string corresponds to the chart options key, such as "claimAnalyticsClaimsChartOptions".
 * An example would be:
 * {
 *   "claimAnalyticsClaimsChartOptions": [
 *     {
 *       id: "y",
 *       optionId: "rollingIPTV",
 *     },
 *   ],
 * }
 */
export type TabChartSettingsState = Record<string, ChartSettingsState>;

/**
 * The settings for the charts on a single page.
 * The page consists of a collection of tabs, each of which has a collection of charts.
 * The string corresponds to the tab key, such as "claims" or "by-vehicle-age".
 * An example would be:
 * {
 *   "claims": {
 *     "claimAnalyticsClaimsChartOptions": [
 *       {
 *         id: "y",
 *         optionId: "rollingIPTV",
 *       },
 *     ],
 *   ],
 * }
 *
 * This is the level of data that is provided by the useFilterSortState hook.
 */
export type PageChartSettingsState = Record<string, TabChartSettingsState>;

/**
 * Asserts that the given object is a PageChartSettingsState.
 * We use this to ensure that the data we get from the query string is valid.
 * This matters because people could type anything into the query string.
 * @param pageChartSettings The type of the parameter has not yet been validated.
 * @throws Error if the data is not valid.
 */
export const assertIsPageChartSettingsState: (
  pageChartSettings: any
) => asserts pageChartSettings is PageChartSettingsState = (
  pageChartSettings
) => {
  if (typeof pageChartSettings !== "object" || pageChartSettings === null) {
    throw new Error("PageChartSettingsState must be an object");
  }

  for (const [tabKey, tabSettings] of Object.entries(pageChartSettings)) {
    if (typeof tabSettings !== "object" || tabSettings === null) {
      throw new Error(`Tab settings for "${tabKey}" must be an object`);
    }

    for (const [chartKey, chartSettings] of Object.entries(tabSettings)) {
      if (!Array.isArray(chartSettings)) {
        throw new Error(
          `Chart settings for "${tabKey}.${chartKey}" must be an array`
        );
      }

      for (const setting of chartSettings) {
        if (typeof setting !== "object" || setting === null) {
          throw new Error(
            `Setting in "${tabKey}.${chartKey}" must be an object`
          );
        }

        if (typeof setting.id !== "string") {
          throw new Error(
            `Setting id in "${tabKey}.${chartKey}" must be a string`
          );
        }

        if (
          typeof setting.optionId !== "string" &&
          typeof setting.optionId !== "number"
        ) {
          throw new Error(
            `Setting optionId in "${tabKey}.${chartKey}" must be a string or number`
          );
        }
      }
    }
  }
};

export interface UseFilterSortState {
  initialized: boolean;
  sort: SortBy | undefined;
  manageOnSortChange: (next: SortBy) => void;
  advancedFilters: FilterGroupState;
  quickFilters: FilterRowState[];
  updateQuickFilters: (
    filters: FilterRowState[],
    outsideChange?: boolean
  ) => void;
  filters: FilterGroupState;
  updateFilters: (
    filters: FilterGroupState,
    callback?: (filters: FilterGroupState) => void,
    outsideChange?: boolean
  ) => void;
  visibleFilters: FilterGroupState;
  manageFilterChange: FilterChangeCallback;
  chartSettings?: PageChartSettingsState;
  manageChartSettingsChange?: ChartSettingsChangeHandler;
  relatedSignalEventsFilter?: OccursFilterState;
  manageRelatedSignalEventsFilterChange?: (next: OccursFilterState) => void;
  resetFilters: (fieldNames?: string[]) => void;
  resetSort: () => void;
  resetFilterSortState: () => void;
  failureModeColumns: string[];
  manageOnVisibleFMColumnChange: (columns: string[]) => void;
  isAdvancedFilterEditor: boolean;
  setIsAdvancedFilterEditor: (
    isAdvanced: boolean,
    clearQuickFilters?: boolean
  ) => void;
  toggleQuickFilters: () => void;
  defaultQuickFilters?: FilterRowState[];
  restoreDefaults: () => void;
  associatedVehiclesState: AssociatedVehiclesState | undefined;
  manageAssociatedVehiclesStateChange: (
    associatedVehiclesState: AssociatedVehiclesState
  ) => void;
  eventEmitter: RefObject<BrowserEventEmitter>;
  defaultFilters?: FilterGroupState;
  staticFilters?: FilterGroupState;
  search?: TableSearch;
  // The filters plus the debounced search, ready to send as the request filter.
  filtersQuery: string;
}

export interface QueryKeys {
  filtersKey: string;
  quickFiltersKey: string;
  sortKey: string;
  columnsKey: string;
  chartSettingsKey: string;
  relatedSignalEventsFilterKey: string;
  isAdvancedEditorKey: string;
  associatedVehiclesStateKey: string;
}

export interface UseInitialStateValuesAndKeysReturn {
  initialValues: FilterSortState;
  queryKeys: QueryKeys;
}

export type InternalFilterStateParams = {
  pageKeyWithVersion: string;
  defaultSort?: SortBy;
  disableUsingQuery?: boolean;
  disableUsingLocalStorage?: boolean;
  defaultFilterValues?: FilterGroupState;
  onlyAdvancedFilters?: boolean;
  defaultAssociatedVehiclesState?: AssociatedVehiclesState;
  currentQuery?: string;
  eventEmitter?: RefObject<BrowserEventEmitter>;
};

export type InternalFilterState = {
  queryKeys: QueryKeys;
  initialized: boolean;
  setInitialized: Dispatch<SetStateAction<boolean>>;
  isAdvancedFilterEditor: boolean;
  setIsAdvancedFilterEditor: Dispatch<SetStateAction<boolean>>;
  filters: FilterGroupState;
  setFilters: Dispatch<SetStateAction<FilterGroupState>>;
  quickFilters: FilterRowState[];
  setQuickFilters: Dispatch<SetStateAction<FilterRowState[]>>;
  sort: SortBy | undefined;
  setSort: Dispatch<SetStateAction<SortBy | undefined>>;
  columns: string[];
  setColumns: Dispatch<SetStateAction<string[]>>;
  chartSettings: PageChartSettingsState | undefined;
  setChartSettings: Dispatch<
    SetStateAction<PageChartSettingsState | undefined>
  >;
  relatedSignalEventsFilter: OccursFilterState | undefined;
  setRelatedSignalEventsFilter: Dispatch<
    SetStateAction<OccursFilterState | undefined>
  >;
  associatedVehiclesState: AssociatedVehiclesState | undefined;
  setAssociatedVehiclesState: Dispatch<
    SetStateAction<AssociatedVehiclesState | undefined>
  >;
};

export interface FilterDiffChange {
  attribute?: boolean;
  operator?: boolean;
  values?: boolean;
  anyAll?: boolean;
  markAll?: boolean;
}

export interface FilterDiffResult {
  deletionChanges: Record<
    number,
    Record<number, Record<number, FilterDiffChange>>
  >;
  additionChanges: Record<
    number,
    Record<number, Record<number, FilterDiffChange>>
  >;
}

export interface FilterDiffData {
  changes: Record<number, Record<number, Record<number, FilterDiffChange>>>;
  bgColor: string;
  // recursion data
  level: number;
  groupIndex: number;
  rowIndex: number;
}
